// The add-ons on /quote-setup ("Customise your cover"), in display order.
//
// `theft: true` marks the ones Theft only can carry — the cover for the rider
// rather than the bike. The rest need Theft + damage and aren't offered on
// Theft only.
//
// Public Liability is sold on its own on Theft + damage only, and is part of
// every Personal Injury plan ("Personal injury & liability"), so UpsellCard
// shows it as included once one is picked.
//
// `value` is what goes into upsellSelections and the pricing payload — keep
// these exactly as the API expects them. Compare with addOnChoice(), not ===:
// the API hands some of them back as numbers and some as strings.

export const ADD_ONS = [
  {
    key: 'accessories_cover',
    title: 'Accessories & gear',
    detail: 'Helmet, lights, clothing and GPS units.',
    tooltip:
      "Accessories are equipment added and fixed to the frame of your bike and which are not essential to the bike's operation. This includes panniers, GPS computers, saddle bags, mudguards, lights, trailers and passenger carrying trailers. Personal Possessions are items of clothing and articles of a strictly personal nature including cycling helmets, cycling clothing and sports equipment. Items which are essential to the bikes operation such as upgraded wheels or gears should be included in the declared replacement value of your bike.",
    choices: [
      { value: 'none', label: 'No cover' },
      { value: 250, label: '£250', popular: true },
      { value: 500, label: '£500' },
      { value: 1000, label: '£1,000' },
    ],
  },
  {
    key: 'personal_injury',
    title: 'Personal injury & liability',
    detail: 'Financial support if you’re hurt while riding.',
    theft: true,
    tooltip:
      'Add personal cover to cover <b>yourself</b> in the event of an accident. This includes compensation for broken bones and other serious injuries, cover against claims from other people for damage to them or their property caused by your negligence and more.<br>Please note: personal cover is for the named policy holder only and does not apply to their family members.',
    choices: [
      { value: 'none', label: 'No cover' },
      { value: 'bronze', label: 'Bronze', popular: true },
      { value: 'silver', label: 'Silver' },
      { value: 'gold', label: 'Gold' },
    ],
  },
  {
    key: 'public_liability',
    title: 'Public liability',
    detail: '£1M protection against third-party claims.',
    tooltip:
      'Public Liability insurance is designed to protect you against claims made by third parties if your use of a bicycle causes accidental injury or property damage. Please note that we are unable to provide public liability cover for any claims arising from the use of your bike in the USA or Canada.',
    choices: [
      { value: 'none', label: 'No cover' },
      { value: '1', label: 'Yes' },
    ],
  },
  {
    key: 'loss_of_earnings',
    title: 'Loss of earnings',
    detail: 'Weekly income if an accident stops you working.',
    theft: true,
    tooltip:
      'Weekly income support if you are unable to work following an accident. Benefits are payable for a maximum period of 52 weeks from the date of the accident, less an excess period of 14 days. In the event of a claim the maximum weekly benefit payable is 75% of your usual average weekly net wage.',
    choices: [
      { value: 'none', label: 'No cover' },
      { value: '200', label: '£200/wk' },
      { value: '500', label: '£500/wk' },
      { value: '750', label: '£750/wk' },
    ],
  },
  {
    key: 'worldwide_cover',
    title: 'Worldwide travel',
    detail: 'Extend your cover outside the UK.',
    tooltip:
      'If you select overseas cover, your entire cover will be extended worldwide for the number of days selected (excluding public liability (if added) in the USA & Canada). In addition, your bike and a bike box valued up to £500 will be covered whilst they are in the care of an airline.',
    choices: [
      { value: 'none', label: 'No cover' },
      { value: 30, label: '30 days' },
      { value: 60, label: '60 days' },
    ],
  },
  {
    key: 'amateur_racing',
    title: 'Competition & racing',
    // Not "sportives" as in the mock: the policy wording (tooltip below)
    // says sportives don't need this cover.
    detail: 'Amateur racing and triathlons.',
    tooltip:
      'Select this if you would like to be covered when competing in an organised cycling or triathlon event where a winner is selected (sportives and charity events do not require this to be selected).<br><br>If you take part in an organised triathlon or duathlon then the bike (if added) will be covered in transition and you (if added) will also be covered in the non-cycling legs (the swim and run).',
    choices: [
      { value: 'none', label: 'No cover' },
      { value: 'yes', label: 'Yes' },
    ],
  },
]

export function isSameChoice(a, b) {
  return String(a) === String(b)
}

export function addOnChoice(addOn, value) {
  return addOn.choices.find((c) => isSameChoice(c.value, value)) || null
}

// The add-ons the given cover type can carry, in display order.
export function addOnsFor(type) {
  return ADD_ONS.filter((a) => type !== 'theft' || a.theft)
}
